import { useContext, useMemo } from 'react';

import { Box, Flex, HStack, SkeletonText, Text, VStack } from '@chakra-ui/react';
import Big from 'big.js';
import { addDays, format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';

import { Maybe, useCreditDecisionDataQuery } from '../../gql/generated/graphql';

import CreditDecisionCommission from './CreditDecisionCommission';
import CreditDecisionInfoRow from './CreditDecisionInfoRow';
import CreditDecisionStatusText from './CreditDecisionStatusText';
import useCreditDecisions, { CreditDecisionWithSubmittedAt } from './useCreditDecisions';

import { CreditDecisionStatusLabelEnum, isCUDL, isR1 } from '../../constants/creditDecisions';
import { DealContext } from '../../libs/DealContext';
import { formatMoney } from '../../libs/utils';

type CreditDecisionDetailsProps = {
  bankName: ReturnType<ReturnType<typeof useCreditDecisions>['getBankNamesByProviderLenderId']>;
  creditDecision?: CreditDecisionWithSubmittedAt;
};

const CreditDecisionDetails = ({ bankName, creditDecision }: CreditDecisionDetailsProps) => {
  const { deal } = useContext(DealContext);
  const { data: creditDecisionData, loading } = useCreditDecisionDataQuery({
    skip: !creditDecision?.id,
    variables: {
      id: Number(creditDecision?.id),
    },
  });

  const notes = useMemo(() => {
    const { application_status, provider_decision_data } =
      creditDecisionData?.creditDecisionV2 || {};
    if (!provider_decision_data) {
      return '';
    }

    const notesBuilder: Maybe<string>[] = [];

    if (isCUDL(provider_decision_data)) {
      // For the async decisions, CUDL usually sends just the current decision in the Decision property.
      const { Decision, Decisions, Comments } = provider_decision_data || {};
      const { Stipulations, Reasons, OtherData } =
        Decision || Decisions?.Decision?.find((d) => d?.IsCurrent) || {};

      // Use the mapped status instead of the CUDL status to be consistent.
      if (application_status) {
        notesBuilder.push(
          `<b>Application Status:</b> ${CreditDecisionStatusLabelEnum[application_status]}`,
        );
      }

      if (Reasons?.DecisionReason && Reasons.DecisionReason.length > 0) {
        notesBuilder.push('<b>Reasons:</b>');
        notesBuilder.push(...Reasons.DecisionReason.map((r) => r?.Description || ''));
      }

      if (
        OtherData?.ReferralReasons?.ReferralReason &&
        OtherData.ReferralReasons.ReferralReason.length > 0
      ) {
        notesBuilder.push('<b>Referral Reasons:</b>');
        notesBuilder.push(
          ...OtherData.ReferralReasons.ReferralReason.map((r) => r?.ReferralReasonText || ''),
        );
      }

      if (Stipulations?.Stipulation && Stipulations.Stipulation.length > 0) {
        notesBuilder.push('<b>Stipulations:</b>');
        notesBuilder.push(...Stipulations.Stipulation);
      }

      if (Comments?.Comment && Comments.Comment.length > 0) {
        notesBuilder.push('<b>Comments:</b>');
        notesBuilder.push(
          ...Comments.Comment.map((c) =>
            c?.Subject && c.Body
              ? `<b>${c.Subject} (${c.Source || 'Unknown'}):</b> ${c.Body || ''}`
              : '',
          ),
        );
      }
    } else if (isR1(provider_decision_data)) {
      const { applicationStatus, applicationStatusDetail } = provider_decision_data || {};
      if (applicationStatus && applicationStatusDetail) {
        notesBuilder.push(
          `<b>Application Status:</b> ${CreditDecisionStatusLabelEnum[applicationStatus]} - ${applicationStatusDetail}`,
        );
      }

      const { conditionRejection, decisionComments, stipulations } =
        provider_decision_data.decision || {};

      if (conditionRejection && conditionRejection.length > 0) {
        notesBuilder.push('<b>Rejection Reasons:</b>');
        notesBuilder.push(...conditionRejection.map((cr) => cr?.conditionRejectionText || ''));
      }

      if (stipulations && stipulations.length > 0) {
        notesBuilder.push('<b>Stipulations:</b>');
        notesBuilder.push(...stipulations);
      }

      if (decisionComments && decisionComments.length > 0) {
        notesBuilder.push('<b>Decision Comments:</b>');
        notesBuilder.push(...decisionComments);
      }
    }

    return notesBuilder.length
      ? DOMPurify.sanitize(notesBuilder.filter(Boolean).join('<br />'))
      : '';
  }, [creditDecisionData?.creditDecisionV2?.provider_decision_data]);

  const sellRate = new Big(creditDecision?.annual_percentage_rate || 0)
    .plus(creditDecision?.max_markup || 0)
    .toNumber();

  return (
    <VStack w="full" p={4} spacing={2}>
      <VStack bg="gray.100" rounded="sm" w="full" p={2}>
        <HStack w="full" alignItems="flex-start" justifyContent="space-between">
          <VStack alignItems="flex-start" spacing={0}>
            <Text fontWeight="bold" fontSize="xl" color="queenBlue">
              {bankName.displayName}
            </Text>
            <Text fontSize="xs">Bank App #: {creditDecision?.provider_decision_id}</Text>
          </VStack>
          <CreditDecisionCommission
            skeletonSize="sm"
            fontSize="md"
            creditDecisionId={creditDecision?.id}
            creditDecisionRevision={creditDecision?.updated_at}
            lenderFees={creditDecision?.lender_fees}
          />
        </HStack>
        <CreditDecisionStatusText creditDecision={creditDecision} />
        <HStack w="full" justifyContent="space-evenly" alignItems="center">
          <VStack alignItems="flex-start">
            <CreditDecisionInfoRow label="Sell Rate" value={sellRate ? `${sellRate}%` : 'N/A'} />
            <CreditDecisionInfoRow
              label="Money Down"
              value={
                deal.financial_info?.money_down
                  ? `${formatMoney(deal.financial_info.money_down, { noDecimal: true })}`
                  : 'N/A'
              }
            />
          </VStack>
          <VStack alignItems="flex-start">
            <CreditDecisionInfoRow
              label="Buy Rate"
              value={
                creditDecision?.annual_percentage_rate
                  ? `${creditDecision.annual_percentage_rate}%`
                  : 'N/A'
              }
            />
            <CreditDecisionInfoRow
              label="Submitted"
              value={
                creditDecision?.submittedAt
                  ? format(parseISO(creditDecision?.submittedAt), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
          </VStack>
          <VStack alignItems="flex-start">
            <CreditDecisionInfoRow
              label="Term"
              value={creditDecision?.term ? `${creditDecision.term} mos` : 'N/A'}
            />
            <CreditDecisionInfoRow
              label="Expires"
              value={
                creditDecision?.submittedAt
                  ? format(addDays(parseISO(creditDecision.submittedAt), 30), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
          </VStack>
        </HStack>
      </VStack>
      {loading ? (
        <SkeletonText noOfLines={5} w="full" />
      ) : (
        <Flex flexDirection="column" gap={2} w="full">
          <Box alignSelf="flex-start">
            <Text fontSize="sm" fontWeight="bold">
              Decision Received:{' '}
              {creditDecision?.created_at
                ? format(
                    parseISO(creditDecision.created_at),
                    "MM/dd/yyyy 'at' h:mmaaa",
                  ).toLowerCase()
                : 'N/A'}
            </Text>
          </Box>
          <Text dangerouslySetInnerHTML={{ __html: notes }} />
        </Flex>
      )}
    </VStack>
  );
};

export default CreditDecisionDetails;
